import {FC, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {FormikProps} from 'formik';
import {Popup, Switcher, PopupFooter} from '@tickchak/react-share/app';
import useShow, {TShow} from '../../../../hooks/useShow';
import KebabDots from '../../../../assets/icons/KebabDots';
import LectureCardMenu from '../lectureCard/lectureCardMenu/LectureCardMenu';
import FormContext from '../../../components/formContext/FormContext';
import DetailsTab from './tabs/DetailsTab';
import TicketsTab from './tabs/TicketsTab';
import AdvancedTab from './tabs/AdvancedTab';
import LecturersSelectPopup from './fields/LecturersSelectPopup';
import HallSelectPopup from './fields/HallSelectPopup';
import TimeSlotSelectPopup from './fields/TimeSlotSelectPopup';
import DaySelectPopup from './fields/DaySelectPopup';
import {LectureFormValues, TabId} from './types';
import {TTimetableItem} from '../../../../redux/timetable/timetable.type';
import {useLectureForm} from './useLectureForm';
import * as css from './ManageLecturePopup.css';

interface ManageLecturePopupProps {
    show: TShow;
    lecture?: TTimetableItem | null;
    defaultTsid?: number | null;
}

const ManageLecturePopup: FC<ManageLecturePopupProps> = ({show, lecture, defaultTsid}) => (
    <Popup show={show} data-cy="manageLecturePop">
        <PopupContent key={String(show.isOpen)} show={show} lecture={lecture ?? null} defaultTsid={defaultTsid ?? null} />
    </Popup>
);

const PopupContent: FC<{show: TShow; lecture: TTimetableItem | null; defaultTsid: number | null}> = ({show, lecture, defaultTsid}) => {
    const {t} = useTranslation('timetable');
    const [activeTab, setActiveTab] = useState<TabId>('details');
    const lecturersShow = useShow();
    const hallShow = useShow();
    const slotShow = useShow();
    const dayShow = useShow();
    const actionsShow = useShow();
    const {initialValues, validationSchema, handleSubmit, isEdit} = useLectureForm(show, lecture, defaultTsid);

    const tabs: {id: TabId; label: string; content: JSX.Element}[] = [
        {id: 'details', label: t('tabDetails'), content: <DetailsTab lecturersShow={lecturersShow} hallShow={hallShow} slotShow={slotShow} dayShow={dayShow} />},
        {id: 'tickets', label: t('tabTickets'), content: <TicketsTab />},
        {id: 'advanced', label: t('tabAdvanced'), content: <AdvancedTab />},
    ];
    const activeTabContent = tabs.find(tab => tab.id === activeTab)?.content;

    return (
        <FormContext
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
            enableReinitialize
        >
            {({submitForm, isSubmitting, values, setFieldValue}: FormikProps<LectureFormValues>) => (
                <Popup.Tabs
                    tabs={tabs}
                    value={activeTab}
                    onChange={v => setActiveTab(v as TabId)}
                    hasInnerPopup
                    header={
                        <Popup.Header
                            title={isEdit ? t('editLectureTitle') : t('manageLecture')}
                            onClose={show.close}
                            endSlot={
                                <css.headerEnd>
                                    <Switcher
                                        checked={values.active}
                                        onChange={e => setFieldValue('active', e.target.checked)}
                                        label={{on: t('toggleActive'), off: t('toggleInactive'), show: true}}
                                        ariaLabel={t('toggleActive')}
                                    />
                                    {lecture && (
                                        <css.kebab type="button" aria-label={t('lectureActions')} onClick={actionsShow.open}>
                                            <KebabDots />
                                        </css.kebab>
                                    )}
                                </css.headerEnd>
                            }
                        />
                    }
                >
                    <css.form placeholder="" onPointerEnterCapture={() => {}} onPointerLeaveCapture={() => {}}>
                        {activeTabContent}
                    </css.form>
                    <PopupFooter
                        startActions={[
                            {label: t('cancel'), variant: 'neutral', onClick: show.close, disabled: isSubmitting},
                            {label: isEdit ? t('saveLecture') : t('createLecture'), onClick: submitForm, disabled: isSubmitting},
                        ]}
                    />
                    <LecturersSelectPopup show={lecturersShow} />
                    <HallSelectPopup show={hallShow} />
                    <TimeSlotSelectPopup show={slotShow} />
                    <DaySelectPopup show={dayShow} />
                    {lecture && <LectureCardMenu lecture={lecture} show={actionsShow} onAfterAction={show.close} />}
                </Popup.Tabs>
            )}
        </FormContext>
    );
};

export default ManageLecturePopup;
